"use client";

import React, { useState } from 'react'
import "./style.css";
import { colors, formatMsg, Helper, permissions, RecordPaymentModal } from '../..';
import { Button, Checkbox, Collapse, Divider } from 'antd';
import BookingSummaryBottomSheet from '../BottomSheets/BookingSummaryBottomSheet';


function page({
    firebase,
    participantCount,
    pricePackDuration,
    slotsPerDay,
    proratedAmount,
    proratedDays,
    priceDetails,
    participantIndex,
    paymentStatus,
    bookingGroupSize,
    invoiceObj,
    centerId,
    bookingStatus
}: any) {
    // Waitlisted bookings created via Option A flow have no invoice yet.
    // Show a placeholder message instead of misleading ₹0.00 totals.
    const isWaitlistedWithoutInvoice = bookingStatus === "WAITLISTED" && !invoiceObj;
    const [mobileViewPaymentBottomSheetOpen, setMobileViewPaymentBottomSheetOpen] = useState(false);
    const [showAdddPaymentModal, setShowAdddPaymentModal] = useState(false);
    const calculateDiscount = (subTotal, discount) => {

        let discountAmount = 0;
        let participantRedemeptionMin = bookingGroupSize;

        if (typeof (discount.redemptions) === 'number') {
            participantRedemeptionMin = Math.min(participantRedemeptionMin, discount.redemptions);
        }

        if (discount?.value && participantIndex < participantRedemeptionMin) {
            switch (discount.type) {
                case "PERCENTAGE":
                    const rawDiscount = (discount.value / 100) * (subTotal);
                    discountAmount = discount.upto ? Math.min(rawDiscount, discount.upto) : rawDiscount;
                    break;

                case "FIXED_AMOUNT":
                    discountAmount = discount.value;
                    break;
            }
        }

        if (discount?.applicableOnSiblings?.length) {
            let siblingDiscount = 0;
            if (participantIndex < Math.min(discount.applicableOnSiblings.length, participantRedemeptionMin)) {
                const sibling = discount.applicableOnSiblings[participantIndex];
                const value = sibling.value ?? 0;
                const upto = sibling.upto;
                switch (discount.type) {
                    case "PERCENTAGE":
                        const raw = (value / 100) * subTotal;
                        siblingDiscount += upto ? Math.min(raw, upto) : raw;
                        break;
                    case "FIXED_AMOUNT":
                        siblingDiscount += value;
                        break;
                }
            }
            discountAmount += siblingDiscount;
        }

        return discountAmount || 0;
    };


    const getFeeAmount = () => {
        try {
            let count = billedCycles;
            return getCycleFee() * count
        } catch (err) {
            return priceDetails?.pricePack?.feeAmount || 0
        }
    }

    // A RECURRING additional charge is billed every cycle, so it multiplies by the cycle count
    // (pricePackDuration) just like the base fee; ONE_TIME charges + registration fees bill once.
    const chargeMultiplier = (fee: any) => (fee?.paymentFrequency === "RECURRING" ? billedCycles : 1);

    // ── Per-cycle model for RECURRING packs ──────────────────────────────────────────────────
    // A recurring booking is billed one cycle per invoice, and the cycles are NOT identical. The
    // backend cron (createInvoicePayload, guarded by cronOpts.recurringCycle) bills:
    //   cycle 1   = base + registration fees + ALL checked additional charges − first-cycle proration
    //   cycles 2+ = base + RECURRING additional charges only
    // Discounts follow the same split: every discount applies to cycle 1, but only PERCENTAGE
    // discounts recur — a fixed-amount ("value") discount is deducted once, on the first invoice.
    const pricePack = priceDetails?.pricePack;
    const isRecurringPack = pricePack?.paymentType === "RECURRING"
        && ["DAILY", "WEEK", "MONTH", "CUSTOM"].includes(pricePack?.frequency);
    // Postpaid bills each period after it ends, for what was taken in it: a per-session pack has no
    // fixed total, and even a fixed per-period fee is only owed for the periods actually billed. So
    // the summary shows the rate and what has been invoiced so far, never a "fee x cycles" projection
    // (which priced a $10-a-session pack as $10 a week). Every per-cycle figure below counts once.
    const isPostpaid = isRecurringPack && pricePack?.paymentTiming === "AFTER";
    const isPerSession = isPostpaid && pricePack?.feeBasis === "PER_SESSION";
    const billedCycles = isPostpaid ? 1 : (Number(pricePackDuration) || 1);
    const totalCycles = billedCycles;
    // invoiceObj is the booking's one invoice, or the aggregate of all of them (invoicesSummary).
    const invoicedSoFar = Number(invoiceObj?.invoicesSummary?.total ?? invoiceObj?.total ?? invoiceObj?.totalAmount ?? 0) || 0;
    const cadenceLabel = () => pricePack?.frequency === "CUSTOM"
        ? Helper.getBillEveryLabel(pricePack)
        : Helper.getBillEveryLabel({ billEveryValue: 1, billEveryUnit: pricePack?.frequency === "DAILY" ? "DAYS" : pricePack?.frequency === "WEEK" ? "WEEKS" : "MONTHS" });
    // Timings booked per day. A RECURRING cycle bills fee × this (one-time packs already carry slots
    // inside pricePackDuration). CUSTOM priced by duration is per window, so it stays at 1, and older
    // bookings without the field bill a single slot — mirrors the backend's recurringSlotMultiplier.
    const slotMultiplier = isRecurringPack
        && !(pricePack?.frequency === "CUSTOM" && pricePack?.durationType === "DURATION")
        && Number.isInteger(Number(slotsPerDay)) && Number(slotsPerDay) > 1
        ? Number(slotsPerDay) : 1;
    const getCycleFee = () => (pricePack?.feeAmount || 0) * slotMultiplier;

    const getFirstCycleSubTotal = () => {
        let total = getCycleFee();
        priceDetails?.registrationFees?.forEach(fee => { total += fee.value || 0 });
        priceDetails?.additionalCharges?.forEach(fee => { if (fee.checked) total += fee.value || 0 });
        return total - (proratedAmount || 0);
    };

    // Registration fees, ONE_TIME charges and the first-cycle proration never repeat.
    const getRecurringCycleSubTotal = () => {
        let total = getCycleFee();
        priceDetails?.additionalCharges?.forEach(fee => {
            if (fee.checked && fee.paymentFrequency === "RECURRING") total += fee.value || 0;
        });
        return total;
    };

    // What ONE later cycle actually bills, taxes included.
    const getRecurringCycleTotal = () => {
        const subTotal = getRecurringCycleSubTotal();
        let beforeTax = subTotal * participantCount;
        priceDetails?.discounts?.forEach(discount => {
            if (discount?.type === "PERCENTAGE") beforeTax -= calculateDiscount(subTotal, discount);
        });
        let withTax = beforeTax;
        priceDetails?.taxes?.forEach(tax => { withTax += (tax.rate / 100) * beforeTax });
        return withTax;
    };

    const getSubTotal = () => {
        let total = 0;
        total += getFeeAmount();
        priceDetails?.registrationFees?.forEach(fee => { total += fee.value || 0 });
        priceDetails?.additionalCharges?.forEach(fee => { if (fee.checked) total += (fee.value || 0) * chargeMultiplier(fee) });
        return total - (proratedAmount || 0);
    }

    // Total discount this discount contributes across the WHOLE booking.
    //
    // The bug this fixes: actualAppliedValue is a SINGLE-CYCLE figure — the backend computes it
    // against the first invoice's line-item subtotal, and a RECURRING pack bills quantity 1 there.
    // Subtracting that one figure from a subtotal that spans every cycle counted a recurring
    // percentage discount once instead of once per cycle, so the booking detail page showed a
    // higher total than checkout and than what was actually billed.
    const getDiscountAmount = (discount) => {
        if (isRecurringPack) {
            // Cycle 1 — every discount applies, percentage and fixed alike. actualAppliedValue is
            // exactly this cycle's amount (same base the backend used), so it keeps the detail page
            // in step with what was charged at booking; recompute only for older bookings that
            // predate the stored value.
            const firstCycle = typeof discount?.actualAppliedValue === 'number'
                ? discount.actualAppliedValue
                : calculateDiscount(getFirstCycleSubTotal(), discount);

            // Cycles 2..N — ONLY percentage discounts recur; a fixed-amount discount is a one-off on
            // the first invoice. Recomputed against the smaller recurring base (no registration fee,
            // no ONE_TIME charges); never reuse actualAppliedValue here, which was measured against
            // the larger first-cycle subtotal. Recomputing per cycle also reproduces the backend's
            // per-invoice `upto` capping.
            const laterCycle = discount?.type === "PERCENTAGE"
                ? calculateDiscount(getRecurringCycleSubTotal(), discount)
                : 0;

            return firstCycle + Math.max(0, totalCycles - 1) * laterCycle;
        }

        // Non-recurring packs bill the whole contract on one invoice — unchanged.
        if (typeof discount.actualAppliedValue === 'number') {
            return discount.actualAppliedValue;
        }
        return calculateDiscount(getSubTotal(), discount);
    };

    const getTotal = () => {
        if (isPostpaid) return invoicedSoFar;
        const subTotal = getSubTotal();
        let totalBeforeTax = subTotal * participantCount;
        priceDetails?.discounts?.forEach(discount => {
            totalBeforeTax -= getDiscountAmount(discount);
        });
        let totalWithTax = totalBeforeTax;
        priceDetails?.taxes?.forEach(tax => {
            totalWithTax += (tax.rate / 100) * totalBeforeTax;
        });
        const projected = totalWithTax || 0;

        // BOOK-565 BUG-5: once a recurring booking has actual per-cycle invoices, its Total must reflect
        // their LIVE totals (a later tax %, extra charge, or per-cycle discount edit changes them) — not
        // the original per-cycle × count projection. invoiceObj.invoicesSummary.total is the live sum of
        // the generated invoices. When every cycle is invoiced, that sum IS the total; mid-contract, add
        // the not-yet-generated cycles back at the projected per-cycle rate so the contract total holds.
        const summary = (invoiceObj as any)?.invoicesSummary;
        if (summary && Number(summary.count) > 1) {
            const cycleCount = Number(pricePackDuration) || Number(summary.count);
            const liveSum = Number(summary.total) || 0;
            if (Number(summary.count) >= cycleCount) return liveSum;      // all cycles invoiced → live sum wins
            // Top up the not-yet-generated cycles at the rate a LATER cycle actually bills. Averaging
            // the projection (projected / cycleCount) blends in cycle 1's registration fee and
            // one-time charges, which no remaining cycle will be charged.
            const perCycle = isRecurringPack
                ? getRecurringCycleTotal()
                : (cycleCount > 0 ? projected / cycleCount : projected);
            return liveSum + Math.max(0, cycleCount - Number(summary.count)) * perCycle;
        }
        return projected;
    };

    const totalDiscount = () => priceDetails?.discounts?.reduce((sum, discount) => sum + getDiscountAmount(discount), 0) || 0;

    const createFeeItem = (key, label, fees, type) => {
        let totalFee = Helper.roundNumberToTwo(
            fees.reduce(
                (fee, obj) => fee + (type === "addi" ? (obj.checked ? (obj.value || 0) * chargeMultiplier(obj) : 0) : obj.value || obj.rate),
                0
            )
        );
        if (type === "dis")
            totalFee = Helper.roundNumberToTwo(totalDiscount());
        return {
            key,
            label: (
                <div className={`global-flex-space-between ${type === "dis" ? "bs-book-green" : ""}`}>
                    <div className="bs-base-price">{label}</div>
                    <div className="bs-base-price">
                        {type === "tax"
                            ? totalFee + "%"
                            : type === "dis"
                                ? "-" + Helper.getCurrency(firebase) + totalFee
                                : Helper.getCurrency(firebase) + participantCount * totalFee}
                    </div>
                </div>
            ),
            children:
                type === "dis" ? (
                    <div>
                        {priceDetails?.discounts.map((discount) => {
                            const disAmt = getDiscountAmount(discount);
                            if (!disAmt) return null;
                            return (
                                <div key={discount.id}>
                                    <div className="global-flex-space-between bs-book-collapse-children bs-book-green">
                                        <div className="bs-base-price-desc bs-book-green">
                                            {discount.name || (discount.rate ? discount.rate + "%" : Helper.getCurrency(firebase) + Helper.decimalFormat(disAmt))}
                                        </div>
                                        <div className="bs-base-price-desc bs-book-green bs-price-wrap">
                                            {Helper.getCurrency(firebase) + Helper.decimalFormat(disAmt)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div>
                        {fees.map((fee) => {
                            if (type === "addi" && !fee.checked) return null;
                            return (
                                <div className="global-flex-space-between bs-book-collapse-children" key={fee.id}>
                                    <div>
                                        <div className="bs-base-price-desc">
                                            {participantCount +
                                                " x " +
                                                (fee.name ||
                                                    (fee.value
                                                        ? Helper.getCurrency(firebase) + fee.value
                                                        : fee.rate + "%"))}
                                        </div>
                                    </div>
                                    <div className="bs-base-price-desc bs-price-wrap">
                                        {fee.rate
                                            ? fee.rate + "%"
                                            : fee.value
                                                ? fee.type === "PERCENTAGE"
                                                    ? fee.value + "%"
                                                    : Helper.getCurrency(firebase) + participantCount * fee.value * chargeMultiplier(fee)
                                                : ""}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ),
            style: { marginBottom: 16 },
            className: type === "dis" ? "bs-book-green" : "",
        };
    };

    const summaryItems = [];
    if (priceDetails?.pricePack) {
        summaryItems.push({
            key: "pricingPack",
            label: (
                <div className="global-flex-space-between">
                    <div className="bs-base-price">Base Price</div>
                    <div className="bs-base-price">
                        {isPostpaid
                            ? formatMsg(isPerSession ? "pricingPackSummary.perSession" : "bookingSummary.perPeriod", {
                                amount: Helper.getCurrency(firebase) + participantCount * getCycleFee(),
                                cadence: cadenceLabel(),
                            })
                            : Helper.getCurrency(firebase) +
                            participantCount *
                            pricePackDuration *
                            getCycleFee()}
                    </div>
                </div>
            ),


            children: (
                <div
                    style={{
                        marginLeft: 28,
                        marginTop: 5,
                        color: "#5B5B5B",
                        textTransform: "capitalize",
                    }}
                    className="global-flex-space-between"
                >
                    <div className="bs-base-price-desc">
                        {(() => {
                            if (isPostpaid) {
                                return formatMsg(isPerSession ? "bookingSummary.postpaidPerSessionNote" : "bookingSummary.postpaidPerPeriodNote",
                                    { cadence: cadenceLabel() });
                            }
                            const frequency = priceDetails?.pricePack?.frequency;
                            const isEntireDuration = frequency === "ENTIRE_PACKAGE_DURATION";
                            const isCustom = frequency === "CUSTOM" && priceDetails?.pricePack?.durationType !== "DURATION";
                            const userTypeName = Helper.getSelectedUserTypeName(firebase, formatMsg("participants"));
                            const pricingPackName = Helper.getPricingPackName2(priceDetails?.pricePack, firebase);

                            if (isEntireDuration || isCustom) {
                                return `${participantCount} ${userTypeName} X ${pricingPackName}`;
                            }
                            const duration = pricePackDuration;
                            const frequencyKey = frequency;
                            const frequencyLabel = formatMsg(frequencyKey + (duration > 1 ? "S" : ""));

                            const slotsLabel = slotMultiplier > 1 ? ` X ${slotMultiplier} ${formatMsg("slotsPerDay")}` : "";
                            return `${participantCount} ${userTypeName} X (${frequency === "CUSTOM" && duration === 1 ? "" : (duration + " ")}${frequencyLabel} X ${pricingPackName}${slotsLabel})`;
                        })()}
                    </div>
                </div>
            ),
            style: { marginBottom: 16 },
        });
    }
    if (priceDetails?.registrationFees?.length) {
        summaryItems.push(
            createFeeItem(
                "registrationFees",
                "Registration Fees",
                priceDetails?.registrationFees,
                "reg"
            )
        );
    }
    if (priceDetails?.additionalCharges?.length) {
        summaryItems.push(
            createFeeItem(
                "additionalCharges",
                "Additional Fees",
                priceDetails?.additionalCharges,
                "addi"
            )
        );
    }
    if (priceDetails?.discounts?.length && totalDiscount() > 0) {
        summaryItems.push(
            createFeeItem(
                "discounts",
                "Discounts",
                priceDetails?.discounts,
                "dis"
            )
        );
    }
    if (priceDetails?.taxes?.length) {
        summaryItems.push(
            createFeeItem(
                "taxes",
                "Taxes",
                priceDetails?.taxes,
                "tax"
            )
        );
    }

    function getMobileSummary() {
        return (
            <div className='bs-mobile-bottom-sheet-summary'>
                <div className='bs-card-padding' style={{ padding: "27px 24px" }}>
                    <div style={{ maxHeight: typeof window != "undefined" ? window.innerHeight - 550 : undefined, overflow: "auto" }}>
                        <Collapse
                            defaultActiveKey={['pricingPack']}
                            ghost
                            items={summaryItems}
                            style={{ padding: 0 }}
                            className='bs-summary-collapse'
                        />
                    </div>
                    <Divider style={{ margin: "12px 0px" }} />
                    {!!(proratedAmount && priceDetails?.pricePack?.frequency !== "DAILY") &&
                        <div className='global-flex-space-between bs-total bs-book-green'>
                            <div>
                                {formatMsg("deductions")}
                                {` (${proratedDays} ${proratedDays === 1 ? "day" : "days"})`}
                            </div>
                            <div>{"- "}{Helper.getCurrency(firebase) + (proratedAmount * participantCount).toFixed(2)}</div>
                        </div>
                    }
                </div>
            </div>
        )
    }

    if (isWaitlistedWithoutInvoice) {
        return (
            <div className="bs-main-card">
                <div className="bs-card-padding" style={{ padding: "27px 24px" }}>
                    <div className='bs-payment-status'>
                        <div>{formatMsg("paymentStatus")}</div>
                        {Helper.getAmountStatusTagForBookingDetailView("pending")}
                    </div>
                    <Divider style={{ margin: "16px 0px" }} />
                    <div style={{
                        padding: "16px",
                        backgroundColor: "#FFF8E7",
                        borderRadius: "8px",
                        color: "#7D5800",
                        fontSize: "14px",
                        lineHeight: "1.5"
                    }}>
                        Payment will be calculated and collected when this booking is confirmed from the waitlist.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="bs-main-card">
                <div className="bs-card-padding" style={{ padding: "27px 24px" }}>
                    <div className='bs-payment-status'>
                        <div>
                            {formatMsg("paymentStatus")}
                        </div>
                        {Helper.getAmountStatusTagForBookingDetailView(paymentStatus || "pending")}
                    </div>

                    <Divider style={{ margin: "16px 0px" }} />
                    <div
                        className="mb-16"
                        style={{
                            color: "#5b5b5b",
                            fontWeight: 400,
                            fontSize: "var(--font-size-16)",
                        }}
                    >
                        {formatMsg("summary")}
                    </div>
                    <div style={{ maxHeight: typeof window !== "undefined" ? window.innerHeight - 550 : undefined, overflow: "auto" }}>
                        <Collapse
                            defaultActiveKey={["pricingPack"]}
                            ghost
                            items={summaryItems}
                            style={{ padding: 0 }}
                            className="bs-summary-collapse"
                        />
                    </div>
                    <Divider style={{ margin: "12px 0px" }} />
                    {!!(proratedAmount && priceDetails?.pricePack?.frequency !== "DAILY") && (
                        <div className="global-flex-space-between bs-total bs-book-green" style={{ marginBottom: 4 }}>
                            <div>
                                {formatMsg("deductions")}
                                {` (${proratedDays} ${proratedDays === 1 ? "day" : "days"})`}
                            </div>
                            <div>
                                {"- "}
                                {Helper.getCurrency(firebase) + (proratedAmount * participantCount).toFixed(2)}
                            </div>
                        </div>
                    )}
                    <div className='bs-amount-details'>
                        <div className="global-flex-space-between bs-total" style={{ fontWeight: 700 }}>
                            <div >{formatMsg(isPostpaid ? "invoicedSoFar" : "total")}</div>
                            <div>
                                {Helper.getCurrency(firebase) + Helper.decimalFormat(getTotal())}
                            </div>
                        </div>
                        {
                            paymentStatus?.toLowerCase() !== "cancelled" &&
                            <div className="global-flex-space-between bs-amount-details-font">
                                <div>{formatMsg("paid")}</div>
                                <div>
                                    {Helper.getCurrency(firebase) + Helper.decimalFormat((invoiceObj?.paid || 0))}
                                </div>
                            </div>
                        }
                        {
                            invoiceObj?.pending > 0 && invoiceObj?.status !== "CANCELLED" &&
                            <div className="global-flex-space-between bs-amount-details-font">
                                <div>{paymentStatus?.toLowerCase() === "due" || paymentStatus?.toLowerCase() === "overdue" || paymentStatus?.toLowerCase() === "partiallyoverdue" ? formatMsg("amountDue") : formatMsg("remaining")}</div>
                                <div>
                                    {Helper.getCurrency(firebase) + Helper.decimalFormat((invoiceObj?.pending || 0))}
                                </div>
                            </div>
                        }
                    </div>
                    {
                        invoiceObj?.pending > 0 && !Helper.isAccount(firebase) && invoiceObj?.status !== "CANCELLED" && permissions.hasPermissionForEntity(firebase, permissions.PERMISSIONS.PAYMENT_RECORD_PAYMENT, centerId ? [centerId] : [])
                        && <Button
                            type='primary'
                            style={{
                                width: "100%",
                                height: "41px",
                                borderRadius: "8px",
                                marginTop: "16px",
                                backgroundColor:
                                    firebase?.schoolConfig?.themeColor
                                        ? firebase.schoolConfig.themeColor
                                        : colors.color_activeColor,
                                color: Helper.getThemeColors(firebase?.schoolConfig?.themeColor).textColor || colors.color_white
                            }}
                            onClick={() => setShowAdddPaymentModal(true)}
                        >
                            {formatMsg("c.recordPayment")}
                        </Button>

                    }
                </div>
            </div>
            <RecordPaymentModal
                open={showAdddPaymentModal}
                setOpen={setShowAdddPaymentModal}
                record={invoiceObj}
                firebase={firebase}
            />
            <BookingSummaryBottomSheet
                headingText={formatMsg('summary')}
                showGeneralBottomSheet={mobileViewPaymentBottomSheetOpen}
                setShowGeneralBottomSheet={setMobileViewPaymentBottomSheetOpen}
                children={getMobileSummary()}
            />
            {
                (!mobileViewPaymentBottomSheetOpen) &&
                <div className='bs-mobile-view-b-fix-pay'
                    style={{
                        height: invoiceObj?.status === "CANCELLED"
                            ? "70px"
                            : invoiceObj?.pending > 0 && !Helper.isAccount(firebase)
                                ? "130px"
                                : "80px"
                    }}
                >
                    <div className='bs-proceed-mobile-bottom'>

                        <div
                            className='bs-package-pricing'
                            style={{ width: "50%" }}
                            onClick={() => setMobileViewPaymentBottomSheetOpen(true)}
                        >
                            {isPostpaid ? formatMsg("invoicedSoFar") : "Total"}
                            <div style={{ fontWeight: 800, marginInlineStart: '10px' }}>
                                {Helper.getCurrency(firebase) + Helper.decimalFormat(getTotal())}
                            </div>
                            <div>
                                <img src='/images/downArrowPurple.png' className='bs-mobile-arrow' />
                            </div>
                        </div>

                        {Helper.getAmountStatusTagForBookingDetailView(paymentStatus || "pending", true)}

                    </div>
                    {
                        paymentStatus?.toLowerCase() !== "cancelled" &&
                        <div className='bs-amount-desc-mobile'>

                            <div className="bs-package-pricing bs-amount-details-font">
                                <div>{formatMsg("paid")}</div>
                                <div style={{ marginInlineStart: '10px' }}>
                                    {Helper.getCurrency(firebase) + Helper.decimalFormat((invoiceObj?.paid || 0))}
                                </div>
                            </div>

                            <div className='bs-vertical-divider' />

                            <div className="bs-package-pricing bs-amount-details-font" style={{ paddingInlineStart: 0 }}>
                                <div>{paymentStatus?.toLowerCase() === "due" || paymentStatus?.toLowerCase() === "overdue" || paymentStatus?.toLowerCase() === "partiallyoverdue"
                                    ? formatMsg("amountDue")
                                    : formatMsg("remaining")}
                                </div>
                                <div style={{ marginInlineStart: '10px' }}>
                                    {Helper.getCurrency(firebase) + Helper.decimalFormat((invoiceObj?.pending || getTotal() - (invoiceObj?.paid || 0) || 0))}
                                </div>
                            </div>

                        </div>
                    }

                    {
                        invoiceObj?.pending > 0 && !Helper.isAccount(firebase) && invoiceObj?.status !== "CANCELLED" &&
                        <Button
                            type='primary'
                            style={{
                                width: "100%",
                                height: "41px",
                                borderRadius: "8px",
                                marginTop: "5px",
                                backgroundColor:
                                    firebase?.schoolConfig?.themeColor
                                        ? firebase.schoolConfig.themeColor
                                        : colors.color_activeColor,
                                color: Helper.getThemeColors(firebase?.schoolConfig?.themeColor).textColor || colors.color_white
                            }}
                            onClick={() => setShowAdddPaymentModal(true)}
                        >
                            {formatMsg("c.recordPayment")}
                        </Button>
                    }
                </div>
            }
        </>
    )
}

export default page
